import React, { memo } from 'react';
import { Progress, Card, CardBody, Button } from '@heroui/react';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  ArrowClockwise,
  Warning,
  EnvelopeSimple
} from '@phosphor-icons/react';
import { useMassMailContext } from '../context/MassMailContext';

export const SendingProgress: React.FC = memo(() => {
  const { state } = useMassMailContext();

  if (!state.isSending && !state.emailSendResults) {
    return null;
  }

  return (
    <Card className="w-full">
      <CardBody className="p-4">
        {/* Sending in progress */}
        {state.isSending && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="animate-spin">
                <Clock size={20} className="text-blue-500" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">E-mails worden verzonden</h4>
                <p className="text-sm text-gray-600">
                  Een moment geduld terwijl we uw e-mails verzenden...
                </p>
              </div>
            </div>
            
            <Progress 
              size="sm"
              color="primary"
              isIndeterminate
              className="w-full"
            />
          </div>
        )}

        {/* Send results */}
        {state.emailSendResults && !state.isSending && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              {state.emailSendResults.success ? (
                <CheckCircle size={20} className="text-green-500" />
              ) : (
                <XCircle size={20} className="text-red-500" />
              )}
              <div>
                <h4 className="font-medium text-gray-900">
                  {state.emailSendResults.success ? 'Verzending voltooid' : 'Verzending mislukt'}
                </h4>
                <p className="text-sm text-gray-600">
                  {state.emailSendResults.message || 'E-mails zijn verzonden'}
                </p>
              </div>
            </div>

            {/* Detailed results */}
            {state.emailSendResults.results && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <div className="flex items-center justify-center mb-1">
                      <CheckCircle size={16} className="text-green-500" />
                    </div>
                    <div className="text-lg font-bold text-green-700">
                      {state.emailSendResults.results.successful || 0}
                    </div>
                    <div className="text-xs text-green-600">Succesvol</div>
                  </div>
                  
                  <div className="p-2 bg-red-50 rounded-lg">
                    <div className="flex items-center justify-center mb-1">
                      <XCircle size={16} className="text-red-500" />
                    </div>
                    <div className="text-lg font-bold text-red-700">
                      {state.emailSendResults.results.failed || 0}
                    </div>
                    <div className="text-xs text-red-600">Mislukt</div>
                  </div>
                  
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center mb-1">
                      <EnvelopeSimple size={16} className="text-gray-500" />
                    </div>
                    <div className="text-lg font-bold text-gray-700">
                      {(state.emailSendResults.results.successful || 0) + (state.emailSendResults.results.failed || 0)}
                    </div>
                    <div className="text-xs text-gray-600">Totaal</div>
                  </div>
                </div>

                {/* Failed emails details */}
                {state.emailSendResults.results.failedEmails && 
                 state.emailSendResults.results.failedEmails.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Warning size={16} className="text-amber-500" />
                      <span className="text-sm font-medium text-gray-900">
                        Mislukte verzendingen:
                      </span>
                    </div>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {state.emailSendResults.results.failedEmails.map((failedEmail: any, index: number) => (
                        <div key={index} className="text-xs text-gray-600 bg-gray-50 p-2 rounded-sm">
                          <div className="font-medium">{failedEmail.email}</div>
                          <div className="text-red-600">{failedEmail.error}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Retry failed emails */}
                {state.emailSendResults.results.failed > 0 && (
                  <Button
                    size="sm"
                    color="warning"
                    variant="bordered"
                    startContent={<ArrowClockwise size={14} />}
                    isDisabled={state.isRetrying}
                    isLoading={state.isRetrying}
                    onPress={() => {
                      // TODO: Implement retry logic
                      console.log('Retry failed emails');
                    }}
                  >
                    Probeer mislukte e-mails opnieuw
                  </Button>
                )}
              </div>
            )}

            {/* Timestamp */}
            {state.emailSendResults.timestamp && (
              <div className="text-xs text-gray-500 text-right">
                Voltooid op {new Date(state.emailSendResults.timestamp).toLocaleString()}
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
});
